import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { LinearProgressGraphic } from './LinearProgressGraphic';
import { IterativeProgressGraphic } from './IterativeProgressGraphic';
import { ScaleProgressGraphic } from './ScaleProgressGraphic';
import { AnimatePresence, motion } from "motion/react";
const capabilities = [
    {
        title: 'Friction to Flow',
        subtitle: 'Get unstuck and deliver at the speed the market demands',
        points: [
            'Your system is too risky, slow, or expensive to change, blocking innovation.',
            "You're shipping but can't prove business impact.",
            "You know AI agents can accelerate delivery but you don't know how to integrate them into your SDLC without creating new risks."
        ],
        color: 'var(--brand-blue)',
        colorGlow: 'var(--brand-blue-glow)',
        colorLight: 'var(--brand-blue-light)',
        Graphic: LinearProgressGraphic,
        sparkPath: "M13.98 6.01998V7.97998H9.36L12.64 11.24L11.24 12.64L7.98 9.35998V14H6V9.35998L2.74 12.64L1.34 11.24L4.62 7.97998H1.49012e-08V6.01998H4.62L1.34 2.73998L2.74 1.33998L6 4.61998V-2.07424e-05H7.98V4.61998L11.24 1.33998L12.64 2.73998L9.36 6.01998H13.98Z",
        caseStudy: {
            company: 'TechCorp',
            description: 'Accelerating Legacy System Modernization with AI-Driven Risk Assessment',
            tags: ['Financial Services', 'Risk Management', 'AI Integration'],
            imageUrl: 'https://images.unsplash.com/photo-1590650589327-3f67c43ad8a2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBjb2xsYWJvcmF0aW9uJTIwb2ZmaWNlfGVufDF8fHx8MTc3Mzg4ODkwNnww&ixlib=rb-4.1.0&q=80&w=1080'
        },
        href: '/contact'
    },
    {
        title: 'Vibe to Value',
        subtitle: 'Drive high velocity innovation with a clear path to production',
        points: [
            'You want high-velocity innovation, whether exploring a new concept, modernizing an existing platform, or automating repeatable engineering work.',
            'Your teams have the room to experiment and iterate, but you want a disciplined approach that leads to production solutions.',
            'You want a clear path from experimentation to hardened, scalable systems that deliver measurable business value.'
        ],
        color: 'var(--brand-green)',
        colorGlow: 'var(--brand-green-glow)',
        colorLight: 'var(--brand-green-light)',
        Graphic: IterativeProgressGraphic,
        sparkPath: "M13.98 6.61998V7.35998H8.9L13.6 9.31998L13.32 10.02L8.6 8.05998L12.2 11.68L11.68 12.2L8.06 8.59998L10.02 13.32L9.32 13.6L7.36 8.89998V13.98H6.62V8.89998L4.66 13.6L3.96 13.32L5.92 8.59998L2.3 12.2L1.78 11.68L5.38 8.05998L0.66 10.02L0.38 9.31998L5.08 7.35998H1.49012e-08V6.61998H5.08L0.38 4.65998L0.66 3.95998L5.38 5.91998L1.78 2.29998L2.3 1.77998L5.92 5.37998L3.96 0.65998L4.66 0.37998L6.62 5.07998V-2.07424e-05H7.36V5.07998L9.32 0.37998L10.02 0.65998L8.06 5.37998L11.68 1.77998L12.2 2.29998L8.6 5.91998L13.32 3.95998L13.6 4.65998L8.9 6.61998H13.98ZM8.22 7.23998V6.73998L8.04 6.29998L7.68 5.93998L7.24 5.75998H6.74L6.3 5.93998L5.94 6.29998L5.76 6.73998V7.23998L5.94 7.67998L6.3 8.03998L6.74 8.21998H7.24L7.68 8.03998L8.04 7.67998L8.22 7.23998Z",
        caseStudy: {
            company: 'InnovateLabs',
            description: 'From AI Prototype to Production Platform in 90 Days',
            tags: ['SaaS', 'Product Development', 'Machine Learning'],
            imageUrl: 'https://images.unsplash.com/photo-1719400471588-575b23e27bd7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BlciUyMGNvZGluZyUyMHdvcmtzcGFjZXxlbnwxfHx8fDE3NzM4MzExNjJ8MA&ixlib=rb-4.1.0&q=80&w=1080'
        },
        href: '/contact'
    },
    {
        title: 'Intent to Enduring',
        subtitle: 'Lead enduring transformation across a complex business',
        points: [
            'You want the value that AI promises, but adoption will be complex — requiring significant changes across business, teams, infrastructure, platforms and products.',
            'The pressure to adopt AI is outpacing your ability to reliably introduce it into your operation.',
            'Your teams are already using AI without the necessary governance or rollback capability to do it safely.',
            'You need AI to deliver real business value without expensive missteps.'
        ],
        color: 'var(--brand-purple)',
        colorGlow: 'var(--brand-purple-glow)',
        colorLight: 'var(--brand-purple-light)',
        Graphic: ScaleProgressGraphic,
        sparkPath: "M13.98 5.74V8.28L7.88 7.38L12.84 11.06L11.04 12.86L7.36 7.9L8.26 14.02H5.72L6.62 7.9L2.94 12.86L1.14 11.06L6.1 7.38L1.49012e-08 8.28V5.74L6.1 6.64L1.14 2.96L2.94 1.16L6.62 6.12L5.72 -9.53674e-07H8.26L7.36 6.12L11.04 1.16L12.84 2.96L7.88 6.64L13.98 5.74Z",
        caseStudy: {
            company: 'GlobalRetail',
            description: 'Scaling AI Strategy Across 500+ Stores and Multiple Countries',
            tags: ['Retail', 'Digital Transformation', 'Enterprise Strategy'],
            imageUrl: 'https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBzdHJhdGVneXxlbnwxfHx8fDE3NzM4OTQ3MzR8MA&ixlib=rb-4.1.0&q=80&w=1080'
        },
        href: '/contact'
    }
];
export default function App() {
    const [activeTab, setActiveTab] = useState(0);
    const tabContentRef = useRef(null);
    const activeCapability = activeTab >= 0 ? capabilities[activeTab] : capabilities[0];
    const Graphic = activeCapability.Graphic;
    // Set initial tab based on screen size
    useEffect(() => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
            setActiveTab(-1); // All closed on mobile
        }
        // Desktop stays at 0 (first tab selected)
    }, []);
    const handleTabClick = (index) => {
        setActiveTab(index);
        // Only scroll on mobile breakpoints
        if (window.innerWidth < 768 && tabContentRef.current) {
            setTimeout(() => {
                const element = tabContentRef.current;
                if (element) {
                    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - 20;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    };
    return (React.createElement("div", { className: "min-h-screen relative", style: { backgroundColor: 'var(--page-background)' } },
        React.createElement(motion.section, { className: "Hero relative overflow-hidden py-20 pt-[136px] md:pt-[116px] flex items-start md:items-center justify-center", style: {
                background: 'radial-gradient(ellipse at top, rgba(0, 113, 206, 0.15) 0%, var(--page-background) 60%)',
                minHeight: '600px'
            }, initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.8 } },
            React.createElement("div", { className: "absolute inset-0 overflow-hidden" }, Array.from({ length: 20 }).map((_, i) => (React.createElement(motion.div, { key: i, className: "absolute w-1 h-1 rounded-full", style: {
                    backgroundColor: i % 3 === 0
                        ? 'var(--brand-blue)'
                        : i % 3 === 1
                            ? 'var(--brand-green)'
                            : 'var(--brand-purple)',
                    opacity: 0.4,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`
                }, animate: {
                    y: [0, -30, 0],
                    opacity: [0.2, 0.6, 0.2]
                }, transition: {
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2
                } })))),
            React.createElement("div", { className: "content relative z-10 w-full" },
                React.createElement(motion.h1, { className: "hero-title", style: {
                        color: 'rgba(255, 255, 255, 0.95)',
                        fontFamily: "'GT Flexa', serif",
                        fontWeight: 'var(--font-weight-medium)',
                        lineHeight: '1.2',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                    }, initial: { y: 30, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { delay: 0.2, duration: 0.8 } }, "Drive Your AI Transformation"),
                React.createElement("div", { className: "flex flex-wrap items-start gap-4 mb-12" },
                    React.createElement(motion.p, { className: "hero-title", style: {
                            color: 'rgba(255, 255, 255, 0.95)',
                            fontFamily: "'GT Flexa', serif",
                            fontWeight: 'var(--font-weight-medium)',
                            lineHeight: '1.2'
                        }, initial: { y: 30, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { delay: 0.3, duration: 0.8 } }, "with"),
                    React.createElement("div", { style: { display: 'inline-block' } },
                        React.createElement(motion.p, { className: "hero-title", style: {
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontFamily: "'GT Flexa', serif",
                                fontWeight: 'var(--font-weight-medium)',
                                lineHeight: '1.2'
                            }, initial: { y: 30, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { delay: 0.4, duration: 0.8 } }, "Confidence"),
                        React.createElement(motion.div, { className: "h-1", style: {
                                background: 'linear-gradient(90deg, var(--brand-blue) 0%, var(--brand-purple) 50%, var(--brand-blue) 100%)',
                                backgroundSize: '200% 100%',
                                width: '100%',
                                boxShadow: '0 0 20px var(--brand-blue-glow)'
                            }, initial: { scaleX: 0, transformOrigin: 'left' }, animate: {
                                scaleX: 1,
                                backgroundPosition: ['0% 0%', '30% 0%', '0% 0%']
                            }, transition: {
                                scaleX: { delay: 0.5, duration: 1 },
                                backgroundPosition: {
                                    delay: 1.5,
                                    duration: 8,
                                    repeat: Infinity,
                                    ease: 'easeInOut'
                                }
                            } }))))),
        React.createElement("section", { className: "diagnosis pb-20 relative overflow-hidden", style: {
                backgroundColor: 'var(--page-background)'
            } },
            React.createElement("div", { className: "absolute inset-0 pointer-events-none" },
                React.createElement(motion.div, { className: "absolute rounded-full", style: {
                        background: `radial-gradient(circle, ${activeCapability.color} 0%, rgba(0, 0, 0, 0) 70%)`,
                        width: '840px',
                        height: '840px',
                        left: '-30px',
                        top: '36px',
                        opacity: 0.14,
                        filter: 'blur(50px)'
                    }, key: activeTab, initial: { opacity: 0, scale: 0.8 }, animate: { opacity: 0.14, scale: 1 }, transition: { duration: 0.6 } })),
            React.createElement("div", { className: "content relative z-10 w-full" },
                React.createElement("div", { className: "flex flex-col gap-8" },
                    React.createElement("div", { className: "hidden md:flex w-full flex-row gap-4" }, capabilities.map((capability, i) => (React.createElement(motion.button, { key: i, className: "text-left rounded-lg border-2 transition-all duration-300", style: {
                            flex: 1,
                            padding: '16px 20px',
                            borderColor: activeTab === i
                                ? 'transparent'
                                : 'rgba(255, 255, 255, 0.1)',
                            backgroundColor: activeTab === i
                                ? `${capability.colorGlow}`
                                : 'rgba(20, 20, 20, 0.4)',
                            backdropFilter: 'blur(10px)'
                        }, onClick: () => setActiveTab(i), whileHover: {
                            borderColor: capability.color,
                            scale: 1.02
                        }, whileTap: { scale: 0.98 } },
                        React.createElement("p", { className: "body-text", style: {
                                width: '100%',
                                maxWidth: '240px',
                                color: activeTab === i ? capability.colorLight : 'rgba(255, 255, 255, 0.6)',
                                fontFamily: "'Avenir', 'Avenir Next', system-ui, -apple-system, sans-serif",
                                fontWeight: 400,
                                lineHeight: '1.5'
                            } }, capability.subtitle))))),
                    React.createElement("div", { className: "flex md:hidden flex-col gap-4 w-full" }, capabilities.map((capability, i) => {
                        const CapabilityGraphic = capability.Graphic;
                        const isOpen = activeTab === i;
                        return (React.createElement("div", { key: i, className: "w-full" },
                            React.createElement(motion.button, { className: "w-full text-left rounded-lg border-2 transition-all duration-300 flex items-start gap-3", style: {
                                    padding: '16px 20px',
                                    borderColor: isOpen
                                        ? 'transparent'
                                        : 'rgba(255, 255, 255, 0.1)',
                                    backgroundColor: isOpen
                                        ? `${capability.colorGlow}`
                                        : 'rgba(20, 20, 20, 0.4)',
                                    backdropFilter: 'blur(10px)'
                                }, onClick: () => setActiveTab(isOpen ? -1 : i), whileTap: { scale: 0.98 } },
                                React.createElement("p", { className: "body-text", style: {
                                        flex: 1,
                                        color: isOpen ? capability.colorLight : 'rgba(255, 255, 255, 0.6)',
                                        fontFamily: "'Avenir', 'Avenir Next', system-ui, -apple-system, sans-serif",
                                        fontWeight: 400,
                                        lineHeight: '1.5'
                                    } }, capability.subtitle),
                                React.createElement("span", null, isOpen ? "-" : "+")),
                            React.createElement(AnimatePresence, null, isOpen && (React.createElement(motion.div, { initial: { height: 0, opacity: 0 }, animate: { height: 'auto', opacity: 1 }, exit: { height: 0, opacity: 0 }, transition: { duration: 0.3 }, style: { overflow: 'hidden' } },
                                React.createElement("div", { className: "pt-6 pb-4" },
                                    React.createElement("h3", { className: "subsection-title", style: {
                                            color: 'rgba(255, 255, 255, 0.95)',
                                            fontFamily: "'GT Flexa', serif",
                                            fontWeight: 'var(--font-weight-medium)',
                                            lineHeight: '1.2',
                                            padding: '0',
                                            marginBottom: '32px'
                                        } }, capability.title),
                                    React.createElement(motion.div, { className: "flex items-center justify-center mb-8", style: {
                                            width: '100%',
                                            maxWidth: '280px',
                                            aspectRatio: '1 / 1',
                                            margin: '0 auto'
                                        }, initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 }, transition: { delay: 0.1, duration: 0.4 } },
                                        React.createElement("div", { style: {
                                                width: '100%',
                                                height: '100%'
                                            } },
                                            React.createElement(CapabilityGraphic, { color: capability.color, colorLight: capability.colorLight }))),
                                    React.createElement("div", { className: "space-y-4 mb-8 w-full" }, capability.points.map((point, j) => (React.createElement(motion.div, { key: j, className: "flex items-start gap-4", initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { delay: 0.1 + j * 0.1 } },
                                        React.createElement(motion.svg, { width: "16", height: "16", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "mt-1.5 flex-shrink-0", style: {
                                                filter: `drop-shadow(0 0 6px ${capability.colorGlow})`
                                            }, whileHover: { scale: 1.3, rotate: 15 } },
                                            React.createElement("path", { d: capability.sparkPath, fill: capability.color })),
                                        React.createElement("p", { className: "body-text", style: {
                                                color: 'rgba(255, 255, 255, 0.8)',
                                                fontFamily: "'Avenir', 'Avenir Next', system-ui, -apple-system, sans-serif",
                                                fontWeight: 500,
                                                lineHeight: '1.5'
                                            } }, point))))),
                                    React.createElement(motion.a, { className: "px-8 py-4 rounded border-2 transition-all duration-300 w-full", style: {
                                            borderColor: capability.color,
                                            color: capability.colorLight,
                                            boxShadow: `0 0 20px ${capability.colorGlow}`,
                                            fontSize: '16px',
                                            fontFamily: "'Avenir', 'Avenir Next', system-ui, -apple-system, sans-serif",
                                            fontWeight: 700
                                        }, whileTap: { scale: 0.95 }, initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.3 }, href: capability.href }, "See How We Do It \u2192")))))));
                    })),
                    React.createElement("div", { className: "hidden md:block w-full", ref: tabContentRef },
                        React.createElement(AnimatePresence, { mode: "wait" },
                            React.createElement(motion.div, { key: activeTab, initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -20 }, transition: { duration: 0.4 } },
                                React.createElement("div", { className: "flex flex-col md:flex-row gap-8 md:gap-10" },
                                    React.createElement("div", { className: "flex flex-col w-full md:flex-[3] md:min-w-[400px]" },
                                        React.createElement("h3", { className: "subsection-title", style: {
                                                color: 'rgba(255, 255, 255, 0.95)',
                                                fontFamily: "'GT Flexa', serif",
                                                fontWeight: 'var(--font-weight-medium)',
                                                lineHeight: '1.2',
                                                padding: '0',
                                                marginBottom: '32px'
                                            } }, activeCapability.title),
                                        React.createElement("div", { className: "space-y-4 mb-12 md:mb-12 w-full md:max-w-[720px]" }, activeCapability.points.map((point, j) => (React.createElement(motion.div, { key: j, className: "flex items-start gap-4", initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { delay: 0.2 + j * 0.1 } },
                                            React.createElement(motion.svg, { width: "16", height: "16", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "mt-1.5 flex-shrink-0", style: {
                                                    filter: `drop-shadow(0 0 6px ${activeCapability.colorGlow})`
                                                }, whileHover: { scale: 1.3, rotate: 15 } },
                                                React.createElement("path", { d: activeCapability.sparkPath, fill: activeCapability.color })),
                                            React.createElement("p", { className: "body-text", style: {
                                                    color: 'rgba(255, 255, 255, 0.8)',
                                                    fontFamily: "'Avenir', 'Avenir Next', system-ui, -apple-system, sans-serif",
                                                    fontWeight: 500,
                                                    lineHeight: '1.5'
                                                } }, point))))),
                                        React.createElement(motion.a, { className: "px-8 py-4 rounded border-2 transition-all duration-300 w-full md:w-fit", style: {
                                                borderColor: activeCapability.color,
                                                color: activeCapability.colorLight,
                                                boxShadow: `0 0 20px ${activeCapability.colorGlow}`,
                                                fontSize: '16px',
                                                fontFamily: "'Avenir', 'Avenir Next', system-ui, -apple-system, sans-serif",
                                                fontWeight: 700
                                            }, whileHover: {
                                                backgroundColor: activeCapability.color,
                                                color: 'rgba(255, 255, 255, 1)',
                                                scale: 1.05,
                                                boxShadow: `0 0 30px ${activeCapability.color}`,
                                                transition: { duration: 0.2 }
                                            }, whileTap: { scale: 0.95 }, initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.5 }, href: activeCapability.href }, "See How We Do It \u2192")),
                                    React.createElement(motion.div, { className: "flex items-center justify-center", style: {
                                            flex: 2,
                                            aspectRatio: '1 / 1',
                                            maxHeight: '336px'
                                        }, initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 }, transition: { delay: 0.2, duration: 0.4 } },
                                        React.createElement("div", { style: {
                                                width: '100%',
                                                height: '100%'
                                            } },
                                            React.createElement(Graphic, { color: activeCapability.color, colorLight: activeCapability.colorLight }))))))))))));
}
export function onDOMLoaded() {
    const capRoot = document.getElementById("ai-capabilities-wrapper");
    if (!capRoot) {
        return;
    }
    const root = createRoot(capRoot);
    root.render(React.createElement(App, null));
}
